"use client";

import React from "react";
import { ArrowRight, BookOpen, CircleCheck as CheckCircle2, Clock, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const fallbackArticles = [{
  id: "fb-1",
  title: "How often should you transition an adult dog to senior nutrition?",
  excerpt: "Most dogs benefit from adjusting caloric density and joint-supporting nutrients between ages 7 and 9, depending on breed size.",
  category: "Nutrition & Diet",
  readTime: "5 min read",
  author: "Dr. Sarah Jenkins, DVM",
  reviewer: "Board-Certified Canine Nutritionist",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/puppy_eating_bowl.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/puppy_eating_bowl.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/puppy_eating_bowl.webp-wvc-sizes",
  link: "/PetHealthPage"
}, {
  id: "fb-2",
  title: "What are the earliest clinical signs of joint stiffness in active dogs?",
  excerpt: "Subtle changes in morning posture, hesitation before jumping, and altered gait patterns typically precede visible limping.",
  category: "Preventive Health",
  readTime: "6 min read",
  author: "Dr. Michael Chen, DVM",
  reviewer: "Veterinary Orthopedic Specialist",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/vet_checkup_dog.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/vet_checkup_dog.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/vet_checkup_dog.webp-wvc-sizes",
  link: "/PetHealthPage"
}, {
  id: "fb-3",
  title: "Why do dogs eat grass, and when does it warrant a clinic visit?",
  excerpt: "While occasional grazing is normal exploratory behavior, sudden obsessive ingestion accompanied by lethargy requires evaluation.",
  category: "Behavior & Safety",
  readTime: "4 min read",
  author: "Dr. Elena Vance, MRCVS",
  reviewer: "Veterinary Internal Medicine",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_resting_park.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_resting_park.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_resting_park.png-wvc-sizes",
  link: "/PetHealthPage"
}];
function QuestionsConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {[0, 1, 2].map((_, i) => <div key={i} data-index={i} className="rounded-xl border border-border bg-card p-6 space-y-4 animate-pulse">
            <div className="h-52 w-full rounded-lg bg-muted" />
            <div className="h-4 w-24 rounded bg-muted" />
            <div className="h-6 w-3/4 rounded bg-muted" />
            <div className="h-16 w-full rounded bg-muted" />
            <div className="h-10 w-full rounded bg-muted" />
          </div>)}
      </div>;
  }
  const displayList = posts && posts.length > 0 ? posts.slice(0, 3).map((post, index) => {
    const fallback = fallbackArticles[index % fallbackArticles.length];
    return {
      id: post.id || `post-${index}`,
      title: post.title || fallback.title,
      excerpt: post.excerpt || fallback.excerpt,
      category: post.categories && post.categories.length > 0 ? post.categories[0].name : fallback.category,
      readTime: fallback.readTime,
      author: post.author?.name || fallback.author,
      reviewer: fallback.reviewer,
      image: post.featuredImage || fallback.image,
      isWpImage: Boolean(post.featuredImage),
      srcset: fallback.srcset,
      sizes: fallback.sizes,
      link: post.link || fallback.link
    };
  }) : fallbackArticles.map(item => ({
    ...item,
    isWpImage: false
  }));
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {displayList.map((article, i) => <article key={article.id} data-index={i} className="flex">
          <Card className="flex flex-col justify-between w-full rounded-xl border border-border bg-card shadow-sm hover:shadow-md hover:border-border/80 transition-[box-shadow,border-color] duration-250 overflow-hidden">
            <div>
              <div className="relative aspect-16/10 w-full overflow-hidden bg-muted border-b border-border/60">
                {article.isWpImage ? <img src={article.image} alt={article.title} loading="lazy" decoding="async" className="h-full w-full object-cover" /> : <img src={article.image} data-wvc-srcset={article.srcset} data-wvc-sizes={article.sizes} alt={article.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />}
                <div className="absolute top-3 left-3">
                  <Badge variant="secondary" className="bg-background/95 text-foreground font-semibold px-2.5 py-1 text-xs backdrop-blur-sm border border-border shadow-xs">
                    {article.category}
                  </Badge>
                </div>
              </div>

              <CardHeader className="pt-5 pb-3 px-6 space-y-2.5">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="size-3.5" />
                  <span>{article.readTime}</span>
                </div>
                <h3 className="font-serif text-lg lg:text-xl font-semibold leading-snug text-card-foreground">
                  <Link to={article.link || "#"} className="hover:text-foreground/80 transition-colors">
                    {article.title}
                  </Link>
                </h3>
              </CardHeader>

              <CardContent className="px-6 py-0">
                <div className="rounded-lg bg-background p-3.5 border-l-4 border-l-primary border border-border/80 text-xs leading-relaxed text-muted-foreground mb-4">
                  <p className="line-clamp-3">{article.excerpt}</p>
                </div>
              </CardContent>
            </div>

            <CardFooter className="px-6 pb-6 pt-2 flex flex-col items-stretch gap-4 border-t border-border/60">
              <div className="flex items-start justify-between gap-2 pt-1 text-xs">
                <div>
                  <p className="font-semibold text-card-foreground">{article.author}</p>
                  <p className="text-muted-foreground text-[11px] flex items-center gap-1 mt-0.5">
                    <CheckCircle2 className="size-3 text-primary shrink-0" />
                    <span>{article.reviewer}</span>
                  </p>
                </div>
              </div>

              <Button asChild variant="outline" size="sm" className="w-full justify-between font-semibold border-border hover:bg-secondary text-card-foreground">
                <Link to={article.link || "#"}>
                  <span>Read full analysis</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </article>)}
    </div>;
}
export default function Questions() {
  const wpQuery = new WP_Query({
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  });
  return <section id="popular-dog-questions" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 lg:mb-16">
          <div className="max-w-2xl space-y-3">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <Sparkles className="size-3.5 text-primary" />
              <span>Evidence-Informed Answers</span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
              Common questions dog owners ask veterinarians
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              Clear, peer-reviewed takeaways on daily canine nutrition, preventive
              care, and common physical signs.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-secondary gap-2">
              <Link to="/PetHealthPage">
                <BookOpen className="size-4" />
                <span>Browse all canine guides</span>
              </Link>
            </Button>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <QuestionsConsumer />
        </PostsProvider>
      </div>
    </section>;
}